// Stage Mail-V2 (2026-07-22 / Step 14): useAttachments — composer の 添付ファイル管理。
//
// 设计(plan §C):
//   - listAttachments(draftId): ページ 起動 時 / 切替 时 fetch → 既存 添付 復元
//   - upload(file[, draftIdOverride]): 25 MiB / MIME 白名单 / 空 file は フロント 侧 で reject
//                 (服务端 二次 防御)
//   - delete(id): 物理 削除 (storage bytes は 残す が row 削除)
//   - 失敗 时 不许 假装 成功
//   - optimistic: list 即时 remove → 失敗 时 refetch revert
//
// 適用: MailV2Detail の Paperclip 接 `<input type="file">`。
// MAIL_V2_REAL=false (demo mode) 时 本 hook は no-op, demoMailData が 提供 する 添付 配列 を その まま 表示。

import { useCallback, useEffect, useRef, useState } from "react";
import { mailV2Api, type MailAttachmentDTO } from "../../../api/mail-v2";

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export interface UseAttachmentsOptions {
  /** null 时 hook 是 idle (no draft selected) */
  draftId: string | null | undefined;
  /** MAIL_V2_REAL=true 时 走真 API;false 时 no-op */
  enabled: boolean;
}

export function useAttachments(opts: UseAttachmentsOptions) {
  const { draftId, enabled } = opts;
  const [items, setItems] = useState<MailAttachmentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef<Set<string>>(new Set());

  // 切 draftId / enabled → 重 fetch
  useEffect(() => {
    if (!enabled || !draftId) {
      setItems([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    mailV2Api
      .listAttachmentsForDraft(draftId)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setItems([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftId, enabled]);

  const upload = useCallback(
    async (file: File, draftIdOverride?: string | null): Promise<void> => {
      const effectiveDraftId = draftIdOverride ?? draftId;
      if (!enabled || !effectiveDraftId) {
        throw new Error("no draft selected");
      }
      if (file.size === 0) {
        throw new Error("空のファイルはアップロードできません");
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        throw new Error(
          `ファイルサイズが上限 ${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MiB を超えています`,
        );
      }
      const tempId = `local:${Date.now()}:${file.name}`;
      if (inFlightRef.current.has(tempId)) return;
      inFlightRef.current.add(tempId);
      setUploading((n) => n + 1);
      setError(null);
      try {
        const created = await mailV2Api.uploadAttachment(file, {
          mailDraftId: effectiveDraftId,
        });
        setItems((prev) => [...prev, created]);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        throw e;
      } finally {
        inFlightRef.current.delete(tempId);
        setUploading((n) => Math.max(0, n - 1));
      }
    },
    [draftId, enabled],
  );

  const remove = useCallback(
    async (attachmentId: string): Promise<void> => {
      if (!enabled) return;
      if (inFlightRef.current.has(attachmentId)) return;
      inFlightRef.current.add(attachmentId);
      // optimistic remove
      const prevItems = items;
      setItems((arr) => arr.filter((a) => a.id !== attachmentId));
      setError(null);
      try {
        await mailV2Api.deleteAttachment(attachmentId);
      } catch (e) {
        // revert on failure
        setItems(prevItems);
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        throw e;
      } finally {
        inFlightRef.current.delete(attachmentId);
      }
    },
    [enabled, items],
  );

  return {
    items,
    loading,
    uploading,
    error,
    upload,
    remove,
  };
}
